// skillTaxonomy.ts — the editorial overlay on the skills table.
//
// The `skills` table has no category column, so category + tags are an editorial
// judgement maintained HERE. Lifted out of SkillsBoardPage (2026-07-26) when the
// skill library became a Board App on the Jarvis board, so the taxonomy outlives
// the page that happened to hold it first.
//
// WHEN YOU MINT A SKILL, ADD IT BELOW. Anything unmapped falls into
// "Uncategorized", which is a deliberate visible nudge rather than a silent
// default — an unfiled skill should look unfiled.
//
// If this list starts drifting from the table the way the memory docs did, the
// fix is a real `category` column on `skills`, not a bigger map.

export const CATEGORY_ORDER = [
  "Design & Content",
  "Dashboard & Boards",
  "Agents & Roles",
  "Client Delivery",
  "Infra & Cost",
  "Voice & Comms",
  "Research & Knowledge",
  // Last on purpose: these are CANDIDATES, not adopted law. They carry
  // status='draft', which is also why the IndexFirst hook now filters to active
  // — a candidate must never read as doctrine an agent may invoke.
  "Skills for Evaluation",
  // THE COLLECTION BUCKET (Erez, 2026-08-01). Skills, plugins and packages
  // found OUTSIDE — dropped in as-is so we can come back and learn from them.
  // Erez's taxonomy: if it ships HOOKS it is a plugin; if not it is a skill you
  // drop into a project to make the project better.
  // These are NOT ours and are NOT law. Every row carries status='draft' so the
  // IndexFirst hook (which filters to active) can never surface one as doctrine.
  // Each is security-audited BEFORE it goes in — that is the entry gate.
  "External Library",
] as const;

export const CATEGORY: Record<string, string> = {
  "design": "Design & Content",
  "content": "Design & Content",
  "nano-banana-diagrams": "Design & Content",
  "pitch-deck": "Design & Content",
  "website-clone": "Design & Content",
  "diagrams": "Design & Content",
  "remotion": "Design & Content",

  "dashboard": "Dashboard & Boards",
  "board": "Dashboard & Boards",
  "dashboard-admin": "Dashboard & Boards",
  "multi-dashboard-rollout": "Dashboard & Boards",

  "jarvis": "Agents & Roles",
  "mjos-algorithm": "Agents & Roles",
  "create-agent": "Agents & Roles",
  "agent-model-switch": "Agents & Roles",
  "echo-relay": "Agents & Roles",
  "head-of-marketing": "Agents & Roles",
  "telos": "Agents & Roles",
  "herdr": "Agents & Roles",
  "project": "Agents & Roles",

  "proposals": "Client Delivery",
  "discovery-analysis": "Client Delivery",
  "scoping": "Client Delivery",
  "paperclip-setup-aws": "Client Delivery",
  "paperclip-google-mcp-setup": "Client Delivery",
  "flameking-manager": "Client Delivery",

  "cloudflare-worker-header-casing": "Infra & Cost",
  "cloudflare-domain-email": "Infra & Cost",
  "neon-cost-audit": "Infra & Cost",
  "workflow-cost-estimator": "Infra & Cost",

  "voice-stack": "Voice & Comms",
  "telegram-agent-bridge": "Voice & Comms",

  "youtube-knowledge-search": "Research & Knowledge",

  // Candidates under evaluation — moved off the retired Skills board 2026-07-26.
  "eval-expressive-typography": "Skills for Evaluation",
  "eval-motion-micro-interactions": "Skills for Evaluation",
  "eval-scroll-storytelling": "Skills for Evaluation",
  "eval-depth-materiality": "Skills for Evaluation",
  "eval-immersive-3d-webgl": "Skills for Evaluation",
  "eval-design-techniques-verdict": "Skills for Evaluation",
  "eval-external-agent-skills": "Skills for Evaluation",

  // ── External Library — collected, not adopted. Prefix: ext- ──
  "ext-agentic-saas-blueprint": "External Library",
};

export const TAGS: Record<string, string[]> = {
  "design": ["design", "tokens", "law"],
  "content": ["copy", "writing", "law"],
  "nano-banana-diagrams": ["images", "gemini", "visual"],
  "pitch-deck": ["deck", "slides", "narrated"],
  "website-clone": ["clone", "recon", "design-lang"],
  "diagrams": ["diagram", "position", "law"],
  "remotion": ["video", "narrated", "code"],
  "dashboard": ["dashboard", "render-paths", "required"],
  "board": ["board", "react-flow", "canvas"],
  "dashboard-admin": ["admin", "tenants", "ops"],
  "multi-dashboard-rollout": ["rollout", "multi-tenant", "deploy"],
  "jarvis": ["chief-of-staff", "doctrine", "verify"],
  "mjos-algorithm": ["7-phase", "tickets", "R&D"],
  "create-agent": ["agent", "provision", "runbook"],
  "agent-model-switch": ["model", "engine", "swap"],
  "echo-relay": ["echo", "voice-relay", "heartbeat"],
  "head-of-marketing": ["role", "marketing", "loop"],
  "telos": ["onboarding", "operator"],
  "herdr": ["desk", "multiplexer", "agents"],
  "project": ["project", "kb-page", "law"],
  "proposals": ["client", "proposal", "workflow"],
  "discovery-analysis": ["discovery", "analysis", "deck"],
  "scoping": ["scope", "questions", "pre-proposal"],
  "paperclip-setup-aws": ["paperclip", "aws", "runbook"],
  "paperclip-google-mcp-setup": ["google-mcp", "sheets", "gmail"],
  "flameking-manager": ["flame-king", "monitor", "client"],
  "cloudflare-worker-header-casing": ["cloudflare", "worker", "debug"],
  "cloudflare-domain-email": ["email", "cloudflare", "routing"],
  "neon-cost-audit": ["neon", "cost", "cleanup"],
  "workflow-cost-estimator": ["cost", "tokens", "estimate"],
  "voice-stack": ["voice", "kokoro", "pipeline"],
  "telegram-agent-bridge": ["telegram", "bridge", "voice-line"],
  "youtube-knowledge-search": ["youtube", "corpus", "search"],

  "eval-expressive-typography": ["candidate", "type", "impact-5"],
  "eval-motion-micro-interactions": ["candidate", "motion", "easy-win"],
  "eval-scroll-storytelling": ["candidate", "scroll", "narrative"],
  "eval-depth-materiality": ["candidate", "depth", "easy-win"],
  "eval-immersive-3d-webgl": ["candidate", "3d", "high-cost"],
  "eval-design-techniques-verdict": ["verdict", "where-to-invest"],
  "eval-external-agent-skills": ["candidate", "external", "agent-skills"],
};

export const ACCENT: Record<string, string> = {
  "Design & Content": "#0E7C66",
  "Dashboard & Boards": "#2F6BB0",
  "Agents & Roles": "#6B4FA0",
  "Client Delivery": "#B85638",
  "Infra & Cost": "#B07A1E",
  "Voice & Comms": "#2C7E9E",
  "Research & Knowledge": "#4E8C4A",
  "Skills for Evaluation": "#8C6BA0",
  "External Library": "#7A6A57",
  "Uncategorized": "#8A948F",
};

export const UNCATEGORIZED = "Uncategorized";

export function categoryOf(slug: string): string {
  return CATEGORY[slug] ?? UNCATEGORIZED;
}
export function accentOf(slug: string): string {
  return ACCENT[categoryOf(slug)] ?? ACCENT[UNCATEGORIZED];
}
export function tagsOf(slug: string): string[] {
  return TAGS[slug] ?? [];
}
